import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { COMMUNITY_LOOKUP_FAILURE_MESSAGE } from 'src/constants/modal.constants'
import { COMMUNITY_LOOKUP_TIMEOUT_MS } from 'src/constants/request.constants'
import { RequestFailureResponse } from 'src/types/plan.interface'

import { COMMUNITY } from '../../lib/api-routes'
import { instance } from '../../lib/axiosInstance'
import { createRequestTimeout } from '../../lib/requestCancellation'
import {
  hasRequestTimedOut,
  REQUEST_TIMEOUT_FAILURE,
} from '../../lib/requestFailure'

export interface CommunityState {
  data: {
    communityCode: string
    communityName: string
    divisionCode: string
    message?: string
    statusCode?: number
  } | null
  error: string | null
  loading: boolean
}

interface CommunityResponse {
  communityInfo: CommunityState['data']
}

const initialState: CommunityState = {
  data: null,
  error: null,
  loading: false,
}

/**
 * Looks a community up by phase number, giving up after
 * `COMMUNITY_LOOKUP_TIMEOUT_MS` so the bulk-copy modal never waits on JDE
 * indefinitely. Every other failure keeps the generic message the modal has
 * always shown; a timeout is reported as such so the user knows to retry.
 */
export const fetchCommunity = createAsyncThunk<
  CommunityResponse,
  string,
  { rejectValue: RequestFailureResponse }
>(
  'communityName/fetchCommunity',
  async (communityPhaseNumber, { rejectWithValue }) => {
    const timeout = createRequestTimeout(COMMUNITY_LOOKUP_TIMEOUT_MS)
    try {
      const response = await instance.get<CommunityResponse>(COMMUNITY, {
        params: {
          communityPhaseNumber,
        },
        signal: timeout.signal,
      })
      return response.data
    } catch (error) {
      return rejectWithValue(
        hasRequestTimedOut(error, timeout)
          ? REQUEST_TIMEOUT_FAILURE
          : { message: COMMUNITY_LOOKUP_FAILURE_MESSAGE }
      )
    } finally {
      timeout.clear()
    }
  }
)

export const communityDataSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommunity.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCommunity.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload?.communityInfo
        state.error = null
      })
      .addCase(fetchCommunity.rejected, (state, action) => {
        state.data = null
        state.loading = false
        state.error =
          action.payload?.message ??
          action.error.message ??
          COMMUNITY_LOOKUP_FAILURE_MESSAGE
      })
  },
  initialState,
  name: 'CommunityName',
  reducers: {},
})

export const { reducer: communityDataReducer } = communityDataSlice
export const getCommunityNameAction = { fetchCommunity }
